<template>
  <div class="promptContainer">
    <VideoImageBackground
      :url="generatedVideoUrl || currentUrl"
      :animate="isPreviewText2Speech"
      :mask="mask"
    />

    <div
      class="generationInProgressContainer"
      v-if="generationInProgress || videoGenerationInProgress"
    >
      <b-progress
        class="progress"
        show-value
        :value="currentProgress"
        type="is-primary"
      >
        {{
          generationInProgress
            ? $t("imageGenerator.generatingImage")
            : $t("imageGenerator.generatingVideo")
        }}
      </b-progress>
    </div>

    <!-- button edit -->
    <div class="buttonEdit" :ignoreImageExport="true">
      <w-button
        :enabled="!generationInProgress"
        :tooltip="$t('imageGenerator.tooltipEditing')"
        icon="pencil"
        size="small"
        color="outlined"
        @click="openPromptEditor"
      ></w-button>
      <w-modal :active="showPrompt" @close="showPrompt = false">
        <CenterCenterPanel v-if="showPrompt">
          <div class="promptEditorContainer">
            <div class="promptEditorTitle">
              {{ $t("imageGenerator.title") }}
            </div>
            <b-tabs v-model="activeTab" class="generatorTabs">
              <!-- TAB 1: GENERATE -->
              <b-tab-item :label="$t('imageGenerator.tabGenerate')">
                <!-- Step 1: Prompt input -->
                <div v-if="generateStep === 'prompt'">
                  <div class="lineHorizontal promptLabelContainer">
                    <div class="labelElement">
                      {{ $t("imageGenerator.prompt") }}
                    </div>
                  </div>

                  <w-input
                    :maxlength="800"
                    class="textareaPrompt"
                    :type="'textarea'"
                    v-model="currentPrompt"
                    :placeholder="$t('imageGenerator.promptPlaceHolder')"
                  />

                  <div class="styleElement">
                    <div class="labelStyle">
                      {{ $t("imageGenerator.style") }}
                    </div>
                    <w-select
                      v-model="styleSelected"
                      @input="saveStyle"
                      size="small"
                    >
                      <option
                        v-for="style in iaStyle"
                        :key="style.value"
                        :value="style.value"
                        :label="style.label"
                      />
                    </w-select>
                  </div>

                  <div class="lineButton">
                    <w-button
                      color="light"
                      class="buttonCancel"
                      @click="showPrompt = false"
                    >
                      {{ $t("generic.cancel") }}
                    </w-button>
                    <w-button
                      icon="auto-fix"
                      class="buttonGenerate"
                      @click="launchImageGeneration"
                    >
                      {{ $t("imageGenerator.generate") }}
                    </w-button>
                  </div>
                </div>

                <!-- Step 2: Image generation in progress -->
                <div
                  v-else-if="generateStep === 'imageGenerating'"
                  class="generationProgress"
                >
                  <b-progress
                    class="progress"
                    show-value
                    :value="fakeprogress"
                    type="is-primary"
                  >
                    {{ $t("imageGenerator.generatingImage") }}
                  </b-progress>
                </div>

                <!-- Step 3: Image preview with Validate / Convert to video -->
                <div
                  v-else-if="generateStep === 'imagePreview'"
                  class="imagePreviewContainer"
                >
                  <div class="imagePreviewWrapper">
                    <img
                      :src="previewImageUrl"
                      class="imagePreview"
                      alt="Generated image"
                    />
                  </div>

                  <div class="lineButton threeButtons">
                    <w-button
                      color="light"
                      class="buttonCancel"
                      @click="backToPrompt"
                    >
                      {{ $t("generic.cancel") }}
                    </w-button>
                    <w-button
                      icon="video-outline"
                      class="buttonConvert"
                      v-if="isImage2VideoGeneration"
                      color="outlined"
                      @click="goToVideoPrompt"
                    >
                      {{ $t("imageGenerator.convertToVideo") }}
                    </w-button>
                    <w-button
                      icon="check"
                      class="buttonGenerate"
                      @click="validateImage"
                    >
                      {{ $t("imageGenerator.validate") }}
                    </w-button>
                  </div>
                </div>

                <!-- Step 4: Video prompt (image shown + prompt below) -->
                <div
                  v-else-if="generateStep === 'videoPrompt'"
                  class="videoPromptContainer"
                >
                  <div class="imagePreviewWrapper">
                    <img
                      :src="previewImageUrl"
                      class="imagePreview"
                      alt="Source image"
                    />
                  </div>

                  <div class="lineHorizontal promptLabelContainer">
                    <div class="labelElement">
                      {{ $t("imageGenerator.videoPrompt") }}
                    </div>
                  </div>

                  <w-input
                    :maxlength="800"
                    class="textareaPrompt"
                    :type="'textarea'"
                    v-model="videoPrompt"
                    :placeholder="$t('imageGenerator.videoPromptPlaceHolder')"
                  />

                  <div class="lineButton">
                    <w-button
                      color="light"
                      class="buttonCancel"
                      @click="backToImagePreview"
                    >
                      {{ $t("generic.cancel") }}
                    </w-button>
                    <w-button
                      icon="auto-fix"
                      class="buttonGenerate"
                      @click="launchVideoGeneration"
                    >
                      {{ $t("imageGenerator.generateVideo") }}
                    </w-button>
                  </div>
                </div>

                <!-- Step 5: Video generation in progress -->
                <div
                  v-else-if="generateStep === 'videoGenerating'"
                  class="generationProgress"
                >
                  <div class="imagePreviewWrapper smallPreview">
                    <img
                      :src="previewImageUrl"
                      class="imagePreview"
                      alt="Source image"
                    />
                  </div>
                  <b-progress
                    class="progress"
                    show-value
                    :value="videoFakeprogress"
                    type="is-primary"
                  >
                    {{ $t("imageGenerator.generatingVideo") }}
                  </b-progress>
                </div>

                <!-- Step 6: Video preview with Validate / Regenerate -->
                <div
                  v-else-if="generateStep === 'videoPreview'"
                  class="videoPreviewContainer"
                >
                  <div class="videoPreviewWrapper">
                    <video
                      :src="generatedVideoUrl"
                      autoplay
                      muted
                      loop
                      playsinline
                      crossorigin="anonymous"
                      class="videoPreview"
                    />
                  </div>

                  <div class="lineButton">
                    <w-button
                      color="light"
                      class="buttonCancel"
                      @click="backToVideoPrompt"
                    >
                      {{ $t("imageGenerator.regenerate") }}
                    </w-button>
                    <w-button
                      icon="check"
                      class="buttonGenerate"
                      @click="validateVideo"
                    >
                      {{ $t("imageGenerator.validate") }}
                    </w-button>
                  </div>
                </div>
              </b-tab-item>

              <!-- TAB 2: UPLOAD -->
              <b-tab-item :label="$t('imageGenerator.tabUploadImage')">
                <!-- Upload zone (shown when not cropping) -->
                <div
                  class="uploadContainer"
                  v-show="!isCropping"
                  v-if="activeTab === 1"
                >
                  <b-upload
                    v-model="uploadedFile"
                    @input="onFileUploaded"
                    accept="image/*"
                    drag-drop
                  >
                    <div class="uploadContent">
                      <w-icon icon="upload-outline" size="large" />
                      <p>{{ $t("imageGenerator.dropOrClick") }}</p>
                    </div>
                  </b-upload>
                </div>

                <!-- Cropper zone (shown when cropping) -->
                <div
                  class="cropperContainer"
                  v-show="isCropping"
                  v-if="activeTab === 1"
                >
                  <div class="cropperImageWrapper">
                    <img
                      :src="fileUploadUrl"
                      class="cropperSource"
                      ref="cropperSource"
                      alt="Image to crop"
                    />
                  </div>
                  <div class="cropperControls">
                    <w-button
                      :circle="true"
                      size="small"
                      @click="resetCropper"
                      icon="aspect-ratio"
                      color="light"
                    />
                    <w-button
                      :circle="true"
                      size="small"
                      @click="rotateLeft"
                      icon="rotate-left"
                      color="light"
                    />
                    <w-button
                      :circle="true"
                      size="small"
                      @click="rotateRight"
                      icon="rotate-right"
                      color="light"
                    />
                    <w-button
                      :circle="true"
                      size="small"
                      @click="zoomPlus"
                      icon="magnify-plus-outline"
                      color="light"
                    />
                    <w-button
                      :circle="true"
                      size="small"
                      @click="zoomMinus"
                      icon="magnify-minus-outline"
                      color="light"
                    />
                  </div>
                  <!-- Hidden preview for cropper -->
                  <img
                    :src="cropperPreviewUrl"
                    class="cropperPreview"
                    ref="cropperPreview"
                    alt="Crop preview"
                  />
                </div>

                <div class="lineButton" v-if="activeTab === 1">
                  <w-button
                    color="light"
                    class="buttonCancel"
                    @click="cancelCropping"
                  >
                    {{ $t("generic.cancel") }}
                  </w-button>
                  <w-button
                    icon="check"
                    class="buttonGenerate"
                    :enabled="isCropping"
                    @click="validateCropping"
                  >
                    {{ $t("imageGenerator.validate") }}
                  </w-button>
                </div>
              </b-tab-item>
            </b-tabs>
          </div>
        </CenterCenterPanel>
      </w-modal>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import VideoImageBackground from "@/components/videoImageBackground/VideoImageBackground.vue";
import WIcon from "@/components/wrapper/w-icon.vue";
import WButton from "@/components/wrapper/w-button.vue";
import WModal from "@/components/wrapper/w-modal.vue";
import CenterCenterPanel from "@/components/layout/CenterCenter.vue";
import WInput from "@/components/wrapper/w-input.vue";
import store from "@/store";
import {
  GENERATE_IMAGE,
  IMAGE_GENERATION_IN_PROGRESS,
} from "@/store/imageGeneration/imageGenerationAction";
import {
  GENERATE_VIDEO,
  VIDEO_GENERATION_IN_PROGRESS,
} from "@/store/videoGeneration/videoGenerationAction";
import { alertError } from "@/utils/dialog";
import delay from "delay";
import WSelect from "@/components/wrapper/w-select.vue";
import { SAVE_STYLE_IMAGEGENERATION } from "@/store/persistedInformation/persistAction";
import WSwitch from "@/components/wrapper/w-switch.vue";
import { IA_STYLE } from "@/utils/ImageUtil";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import debounce from "lodash/debounce";
import { proxify } from "@/utils/proxyUtil";
import { getThumbnailURL } from "@/utils/util";
import {
  SpeachEvent,
  SpeachEventAvatarDuration,
  SpeachEventName,
  SpeachEventVideoDuration,
} from "@/speach/speachEvent";

type GenerateStep =
  | "prompt"
  | "imageGenerating"
  | "imagePreview"
  | "videoPrompt"
  | "videoGenerating"
  | "videoPreview";

@Component({
  components: {
    WSwitch,
    WSelect,
    WInput,
    CenterCenterPanel,
    WModal,
    WButton,
    WIcon,
    VideoImageBackground,
  },
})
export default class VideoImageGenerator extends Vue {
  @Prop({ required: true })
  private url!: string;

  @Prop({ default: "none" })
  private mask!: "none" | "light" | "dark";

  @Prop({ default: false })
  private prompt!: string;

  @Prop({ default: "square" })
  private orientation!: "portrait" | "landscape" | "square";

  private showPrompt: boolean = false;
  private currentPrompt: string = "";
  private currentUrl: string = "";
  private generationInProgress: boolean = false;

  // Generate tab step management
  private generateStep: GenerateStep = "prompt";

  // Fake progress for image generation
  private fakeprogress = 0;
  private PROGRESS_INTERVAL: number = 100;
  private PROGRESS_FAKE_TIME: number = 8000;

  private styleSelected: string = this.iaStyle[0].value;
  private activeTab: number = 0;
  private uploadedFile: File | null = null;

  // Cropper state
  private cropper: Cropper | null = null;
  private fileUploadUrl: string = "";
  private cropperPreviewUrl: string = "";
  private isCropping: boolean = false;

  // Video generation state
  private videoGenerationInProgress: boolean = false;
  private generatedVideoUrl: string = "";
  private videoPrompt: string = "";
  private videoFakeprogress: number = 0;
  private VIDEO_PROGRESS_INTERVAL: number = 100;
  private VIDEO_PROGRESS_FAKE_TIME: number = 120000; // 2 minutes max

  // Image preview state
  private previewImageUrl: string = "";

  get isPreviewText2Speech() {
    return store.getters.isPreviewSpeech2Text;
  }

  get iaStyle() {
    return IA_STYLE;
  }

  get currentProgress(): number {
    return this.generationInProgress
      ? this.fakeprogress
      : this.videoFakeprogress;
  }

  get isImage2VideoGeneration() {
    return store.getters.isImage2VideoGeneration;
  }

  async mounted() {
    this.currentPrompt = this.prompt;
    this.currentUrl = this.url;
    this.styleSelected = store.getters.getStyleImageGeneration;
    this.$nextTick(async () => {
      if (!this.url && this.currentPrompt) {
        await this.launchImageGeneration();
        this.validateImage();
      }
    });
  }

  @Watch("url")
  onUrlUpdated() {
    this.currentPrompt = this.prompt;
    this.currentUrl = this.url;
    this.styleSelected = store.getters.getStyleImageGeneration;
    this.$nextTick(() => {
      if (!this.url && this.currentPrompt) {
        this.launchImageGeneration();
      }
    });
  }

  // ==================== Generate Tab Methods ====================

  private async launchImageGeneration() {
    this.generateStep = "imageGenerating";
    this.generationInProgress = true;
    this.previewImageUrl = "";

    try {
      this.launchFakeProgress();
      const data = await store.dispatch(GENERATE_IMAGE, {
        prompt: this.currentPrompt,
        style: this.styleSelected,
        orientation: this.orientation,
      });

      this.generationInProgress = false;
      store.dispatch(IMAGE_GENERATION_IN_PROGRESS, false);
      this.previewImageUrl = "data:image/jpg;base64," + data;
      this.generateStep = "imagePreview";
    } catch (e) {
      store.dispatch(IMAGE_GENERATION_IN_PROGRESS, false);
      this.generationInProgress = false;
      this.generateStep = "prompt";
      alertError(this.$t("imageGenerator.error").toString());
    }
  }

  private validateImage() {
    this.$emit("imageGenerated", {
      prompt: this.currentPrompt,
      image: this.previewImageUrl,
    });
    this.resetGenerateState();
    this.showPrompt = false;
  }

  private goToVideoPrompt() {
    this.videoPrompt = this.currentPrompt; // Pre-fill with image prompt
    this.generateStep = "videoPrompt";
  }

  private backToPrompt() {
    this.generateStep = "prompt";
    this.previewImageUrl = "";
  }

  private backToImagePreview() {
    this.generateStep = "imagePreview";
  }

  private backToVideoPrompt() {
    this.generatedVideoUrl = "";
    this.generateStep = "videoPrompt";
  }

  private async launchVideoGeneration() {
    this.generateStep = "videoGenerating";
    this.videoGenerationInProgress = true;
    this.generatedVideoUrl = "";

    try {
      this.launchVideoFakeProgress();

      // Build request payload with the generated image
      const payload: { prompt: string; duration: number; imageB64?: string } = {
        prompt: this.videoPrompt,
        duration: 8,
      };

      // Add image from preview
      if (this.previewImageUrl) {
        let imageB64 = this.previewImageUrl;
        if (imageB64.startsWith("data:")) {
          imageB64 = imageB64.split(",")[1];
        }
        payload.imageB64 = imageB64;
      }

      const output = await store.dispatch(GENERATE_VIDEO, payload);

      // send time to speach (stat Speach)
      const dataEvent = new SpeachEventVideoDuration(
        store.getters.getEditingWeetID,
        7
      );
      window.parent.postMessage(
        new SpeachEvent(SpeachEventName.VIDEO_GENERATION_DURATION, dataEvent),
        "*"
      );

      this.videoGenerationInProgress = false;
      this.generatedVideoUrl = proxify(output.url);
      store.dispatch(VIDEO_GENERATION_IN_PROGRESS, false);
      this.generateStep = "videoPreview";
    } catch (e) {
      store.dispatch(VIDEO_GENERATION_IN_PROGRESS, false);
      this.videoGenerationInProgress = false;
      this.generateStep = "videoPrompt";
      alertError(this.$t("imageGenerator.videoError").toString());
    }
  }

  private validateVideo() {
    this.$emit("videoGenerated", {
      prompt: this.videoPrompt,
      video: this.generatedVideoUrl,
      image: this.previewImageUrl,
    });
    this.resetGenerateState();
    this.showPrompt = false;
  }

  private resetGenerateState() {
    this.generateStep = "prompt";
    this.previewImageUrl = "";
    this.generatedVideoUrl = "";
    this.videoPrompt = "";
  }

  // ==================== Progress Methods ====================

  private saveStyle(value: string) {
    store.dispatch(SAVE_STYLE_IMAGEGENERATION, value);
  }

  private async launchFakeProgress() {
    this.fakeprogress = 0;
    for (
      let time = 0;
      time < this.PROGRESS_FAKE_TIME;
      time = time + this.PROGRESS_INTERVAL
    ) {
      await delay(this.PROGRESS_INTERVAL);
      this.fakeprogress = (time / this.PROGRESS_FAKE_TIME) * 100;
      if (!this.generationInProgress) {
        return;
      }
    }
  }

  private async launchVideoFakeProgress() {
    this.videoFakeprogress = 0;
    for (
      let time = 0;
      time < this.VIDEO_PROGRESS_FAKE_TIME;
      time = time + this.VIDEO_PROGRESS_INTERVAL
    ) {
      await delay(this.VIDEO_PROGRESS_INTERVAL);
      this.videoFakeprogress = (time / this.VIDEO_PROGRESS_FAKE_TIME) * 100;
      if (!this.videoGenerationInProgress) {
        return;
      }
    }
  }

  // ==================== Modal Methods ====================

  private async openPromptEditor() {
    // If there's already an image, go directly to image preview step
    if (this.currentUrl) {
      if (this.isVideo(this.currentUrl)) {
        const parsedUrl = new URL(this.currentUrl);
        parsedUrl.search = "";
        parsedUrl.pathname = parsedUrl.pathname.replace(
          /\/[^/]+$/,
          "/thumbnail.jpg"
        );

        const response = await fetch(parsedUrl);
        if (!response.ok) {
          // image not found
          console.warn("Image not found:", parsedUrl);
        } else {
          const blob = await response.blob();
          // Convert the blob to base64
          const reader = new FileReader();
          reader.readAsDataURL(blob);

          await new Promise((resolve) => {
            reader.onloadend = () => {
              // Get the base64 string (remove the data URL prefix)
              const base64data = reader.result as string;
              // remove mimetype
              this.previewImageUrl =
                "data:image/png;base64," + base64data.split("base64,")[1];
              resolve(null);
            };
          });
        }
      } else {
        this.previewImageUrl = this.currentUrl;
      }
      this.generateStep = "imagePreview";
    }
    this.showPrompt = true;
  }

  @Watch("showPrompt")
  onShowPromptChanged(newVal: boolean) {
    // Clean up when modal closes
    if (!newVal) {
      if (this.isCropping) {
        this.cleanupCropper();
      }
      this.resetGenerateState();
    }
  }

  // ==================== Upload Tab / Cropper Methods ====================

  get cropperSourceImage(): HTMLImageElement {
    return this.$refs.cropperSource as HTMLImageElement;
  }

  get cropperAspectRatio(): number {
    switch (this.orientation) {
      case "portrait":
        return 9 / 16;
      case "landscape":
        return 16 / 9;
      case "square":
      default:
        return 1;
    }
  }

  private onFileUploaded(file: File) {
    if (file) {
      this.setupCropper(file);
    }
  }

  private setupCropper(selectedFile: File) {
    // Destroy existing cropper first
    if (this.cropper) {
      this.cropper.destroy();
    }

    if (this.fileUploadUrl) {
      window.URL.revokeObjectURL(this.fileUploadUrl);
      this.fileUploadUrl = "";
    }

    if (!selectedFile) {
      this.cropperPreviewUrl = "";
      this.isCropping = false;
      this.cropper = null;
      return;
    }

    this.fileUploadUrl = window.URL.createObjectURL(selectedFile);
    this.isCropping = true;

    // Wait for Vue to render the new DOM element
    this.$nextTick(() => {
      this.cropper = new Cropper(this.cropperSourceImage, {
        aspectRatio: this.cropperAspectRatio,
        viewMode: 0,
        dragMode: "move",
        autoCropArea: 1,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        crop: debounce(this.updateCropperPreview, 200),
      });
    });
  }

  private updateCropperPreview() {
    if (this.cropper) {
      const canvas = this.cropper.getCroppedCanvas();
      if (canvas) {
        this.cropperPreviewUrl = canvas.toDataURL("image/png");
      }
    }
  }

  private resetCropper() {
    if (this.cropper) {
      this.cropper.reset();
    }
  }

  private rotateLeft() {
    if (this.cropper) {
      this.cropper.rotate(-90);
    }
  }

  private rotateRight() {
    if (this.cropper) {
      this.cropper.rotate(90);
    }
  }

  private zoomPlus() {
    if (this.cropper) {
      this.cropper.zoom(0.2);
    }
  }

  private zoomMinus() {
    if (this.cropper) {
      this.cropper.zoom(-0.2);
    }
  }

  private validateCropping() {
    if (this.cropper) {
      const croppedCanvas = this.cropper.getCroppedCanvas();

      if (!croppedCanvas) {
        this.cleanupCropper();
        return;
      }

      // Create a new canvas with white background
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = croppedCanvas.width;
      finalCanvas.height = croppedCanvas.height;

      const ctx = finalCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
        ctx.drawImage(croppedCanvas, 0, 0);
      }

      const result = finalCanvas.toDataURL("image/png");

      this.cleanupCropper();
      this.previewImageUrl = result;
      this.currentPrompt = "";
      this.activeTab = 0;
      this.generateStep = "imagePreview";
    }
  }

  private cancelCropping() {
    this.cleanupCropper();
    this.showPrompt = false;
  }

  private isVideo(url: string): boolean {
    try {
      const path = new URL(url).pathname;
      return path.endsWith(".mp4") || path.endsWith(".webm");
    } catch {
      return this.url.endsWith(".mp4") || this.url.endsWith(".webm");
    }
  }

  private cleanupCropper() {
    if (this.cropper) {
      try {
        this.cropper.destroy();
      } catch (e) {
        // Cropper might fail to destroy if DOM element was already removed
      }
      this.cropper = null;
    }
    if (this.fileUploadUrl) {
      window.URL.revokeObjectURL(this.fileUploadUrl);
      this.fileUploadUrl = "";
    }
    this.cropperPreviewUrl = "";
    this.isCropping = false;
    this.uploadedFile = null;
  }
}
</script>

<style lang="scss" scoped>
.promptContainer {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;

  .generationInProgressContainer {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    z-index: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);

    .progress {
      width: 80%;
    }
  }

  .lineHorizontal {
    display: flex;
    width: 100%;
    align-items: center;
  }

  .buttonEdit {
    z-index: 1;
    position: absolute;
    top: 16px;
    right: 16px;
  }

  .promptEditorContainer {
    background: white;
    width: 600px;
    border-radius: 12px;
    color: var(--black);
    padding: 32px;

    .promptEditorTitle {
      font-size: 24px;
      font-weight: 900;
      margin-bottom: 32px;
    }

    .lineButton {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 16px;
      margin-top: 24px;

      &.threeButtons {
        justify-content: space-between;

        .buttonCancel {
          margin-right: auto;
        }
      }
    }

    .promptLabelContainer {
      margin-top: 24px;
    }

    .lineHorizontal {
      display: flex;
      width: 100%;
      margin-bottom: 8px;

      .labelElement {
        flex: 1;
        text-align: left;
        font-size: 16px;
        font-weight: 700;
      }
    }

    .styleElement {
      text-align: left;

      .labelStyle {
        font-size: 16px;
        font-weight: 700;
        margin-right: 8px;
      }

      margin-bottom: 16px;
    }

    .textareaPrompt {
      margin-bottom: 8px;
    }

    .generatorTabs {
      margin-top: 16px;
    }

    // Upload tab styles
    .uploadContainer {
      padding: 24px 0;

      .uploadContent {
        display: flex;
        width: 528px;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 48px;
        border: 2px dashed var(--light1);
        border-radius: 8px;
        cursor: pointer;
        transition: border-color 0.2s;

        &:hover {
          border-color: var(--primary);
        }

        p {
          margin-top: 16px;
          font-size: 14px;
          color: var(--dark);
        }
      }
    }

    .cropperContainer {
      padding: 24px 0;
      position: relative;

      .cropperImageWrapper {
        width: 100%;
        max-height: 350px;
        overflow: hidden;
        border-radius: 8px;
        background: var(--light2);

        .cropperSource {
          display: block;
          max-width: 100%;
          max-height: 350px;
        }
      }

      .cropperControls {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-top: 16px;
      }

      .cropperPreview {
        display: none;
      }
    }

    // Generate tab styles
    .generationProgress {
      padding: 48px 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 24px;

      .progress {
        width: 100%;
      }

      .smallPreview {
        max-height: 150px;

        .imagePreview {
          max-height: 150px;
        }
      }
    }

    .imagePreviewContainer,
    .videoPromptContainer {
      padding: 24px 0;

      .imagePreviewWrapper {
        width: 100%;
        max-height: 350px;
        overflow: hidden;
        border-radius: 8px;
        background: var(--light2);
        display: flex;
        align-items: center;
        justify-content: center;

        .imagePreview {
          display: block;
          max-width: 100%;
          max-height: 350px;
          border-radius: 8px;
        }
      }
    }

    .videoPromptContainer {
      .imagePreviewWrapper {
        max-height: 200px;
        margin-bottom: 16px;

        .imagePreview {
          max-height: 200px;
        }
      }
    }

    .videoPreviewContainer {
      padding: 24px 0;

      .videoPreviewWrapper {
        width: 100%;
        max-height: 350px;
        overflow: hidden;
        border-radius: 8px;
        background: var(--light2);
        display: flex;
        align-items: center;
        justify-content: center;

        .videoPreview {
          display: block;
          max-width: 100%;
          max-height: 350px;
          border-radius: 8px;
        }
      }
    }
  }
}
</style>
